import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

// Maps app_settings keys (Settings → Branding & Theme) to the CSS custom
// property they override. One premium dark theme app-wide — no light/dark
// toggle — so all of these always apply.
const CUSTOM_COLOR_VARS = {
  theme_brand_1: '--brand-1',
  theme_brand_2: '--brand-2',
  theme_text_primary: '--text-primary',
  theme_text_muted: '--text-muted',
  theme_border: '--border',
  theme_surface_page: '--surface-page',
  theme_surface_card: '--surface-card',
  theme_surface_sidebar: '--surface-sidebar',
};

// The 12 semantic status variables. When "Use Brand Colour For Status
// Indicators" is on, every one of these is redirected to a brand-derived
// value instead of its theme.css default (green/amber/red/blue) — every
// Badge/StatusBadge/chart/progress-bar/notification that already reads
// var(--success) etc. picks this up automatically, no per-component change.
const STATUS_VARS_TO_BRAND = {
  '--success': 'var(--brand-1)', '--success-bg': 'var(--accent-dim)', '--success-text': 'var(--brand-2)',
  '--warning': 'var(--brand-1)', '--warning-bg': 'var(--accent-dim)', '--warning-text': 'var(--brand-2)',
  '--danger':  'var(--brand-1)', '--danger-bg':  'var(--accent-dim)', '--danger-text':  'var(--brand-2)',
  '--info':    'var(--brand-1)', '--info-bg':    'var(--accent-dim)', '--info-text':    'var(--brand-2)',
};

export function ThemeProvider({ children }) {
  const [customTheme, setCustomTheme] = useState({});
  // Local-only overrides applied instantly (before/without a server round
  // trip) so colour pickers feel live. Cleared once a real fetch confirms them.
  const [liveOverrides, setLiveOverrides] = useState({});

  const fetchCustomTheme = useCallback(async () => {
    try {
      const res = await fetch('/api/settings/theme');
      if (!res.ok) return;
      const data = await res.json();
      setCustomTheme(data.theme || {});
      setLiveOverrides({});
    } catch {
      // best-effort — falls back to theme.css defaults
    }
  }, []);

  useEffect(() => { fetchCustomTheme(); }, [fetchCustomTheme]);

  const useBrandForStatus = (liveOverrides.theme_use_brand_for_status ?? customTheme.theme_use_brand_for_status) === 'true';

  useEffect(() => {
    const root = document.documentElement.style;
    const merged = { ...customTheme, ...liveOverrides };
    for (const [key, cssVar] of Object.entries(CUSTOM_COLOR_VARS)) {
      const value = merged[key];
      if (value) root.setProperty(cssVar, value);
      else root.removeProperty(cssVar);
    }
    for (const [cssVar, brandValue] of Object.entries(STATUS_VARS_TO_BRAND)) {
      if (useBrandForStatus) root.setProperty(cssVar, brandValue);
      else root.removeProperty(cssVar);
    }
  }, [customTheme, liveOverrides, useBrandForStatus]);

  // Instantly reflect a colour-field edit on-screen (Settings → Branding
  // calls this on every keystroke/pick) without waiting for the save to
  // round-trip — the actual persistence + debounce lives in the page.
  const setLiveOverride = useCallback((key, value) => {
    setLiveOverrides(prev => ({ ...prev, [key]: value }));
  }, []);

  const clearLiveOverride = useCallback((key) => {
    setLiveOverrides(prev => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  let presets = [];
  try { presets = JSON.parse(customTheme.theme_presets || '[]'); } catch { presets = []; }

  return (
    <ThemeContext.Provider value={{
      logoUrl: customTheme.theme_logo_url || null,
      customTheme,
      refreshCustomTheme: fetchCustomTheme,
      useBrandForStatus,
      setLiveOverride,
      clearLiveOverride,
      presets,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
